import type { IOverlayElement } from "./Overlay.interface";

/**
 * A panel is a free-form composition surface that lives bound to a specific
 * Area (vs overlays which span the whole frame). Structurally identical to
 * a custom overlay's options — the same `IOverlayElement[]` powers both, so
 * the same renderers (Pixi `CustomOverlayRenderer`, DOM `OverlayElementRenderer`)
 * can draw panels and overlays alike.
 *
 * The legacy typed shapes (`lower_third` / `label` / `stats`) are migrated
 * to this free-form shape on read via `migrateLegacyPanel`. New panels are
 * always free-form.
 */
export interface IStudioPanel {
  id: string;
  label: string;
  active: boolean;
  options: {
    elements: IOverlayElement[];
    /**
     * Optional aspect ratio hint for the canvas. Defaults to "16:9". Used
     * by the PanelBuilder canvas + the PanelStore preview renderer.
     */
    designAspectRatio?: string;
    /**
     * Optional canvas-level background. Renders behind the elements stack.
     * Either solid color or remote image URL.
     */
    background?: {
      color?: string;
      image?: string;
    };
    /**
     * Registry of user-created layers (groups), including empty ones, in order.
     * Mirrors `IStudioOverlayCustom.options.groups` so the shared layers panel
     * (BuilderLayers) gives panels the same layer/sub-layer organization.
     */
    groups?: string[];
  };
}

// ---------------------------------------------------------------------------
// Legacy types — kept for the on-read migrator. Do NOT use in new code.
// ---------------------------------------------------------------------------

/** @deprecated Legacy panel categories. Migrated to free-form on read. */
export enum EStudioPanelType {
  LOWER_THIRD = "lower_third",
  LABEL = "label",
  STATS = "stats",
}

interface ILegacyPanelLowerThird {
  id: string;
  label: string;
  active: boolean;
  type: EStudioPanelType.LOWER_THIRD;
  options: {
    title: string;
    subtitle?: string;
    fontFamily?: string;
    titleColor?: string;
    subtitleColor?: string;
    backgroundColor?: string;
    backgroundImage?: string;
    align: "left" | "right" | "center";
  };
}

interface ILegacyPanelLabel {
  id: string;
  label: string;
  active: boolean;
  type: EStudioPanelType.LABEL;
  options: {
    text: string;
    fontFamily?: string;
    fontSize?: string;
    fontColor?: string;
    backgroundColor?: string;
    padding?: number;
    borderRadius?: number;
  };
}

interface ILegacyPanelStats {
  id: string;
  label: string;
  active: boolean;
  type: EStudioPanelType.STATS;
  options: {
    fields: Array<{ label: string; value: string; icon?: string }>;
    layout: "horizontal" | "vertical";
    fontFamily?: string;
    fontColor?: string;
    backgroundColor?: string;
  };
}

type ILegacyPanel =
  | ILegacyPanelLowerThird
  | ILegacyPanelLabel
  | ILegacyPanelStats;

const isLegacyPanel = (input: unknown): input is ILegacyPanel => {
  if (!input || typeof input !== "object") return false;
  const obj = input as { type?: unknown; options?: unknown };
  return (
    typeof obj.type === "string" &&
    ["lower_third", "label", "stats"].includes(obj.type) &&
    typeof obj.options === "object" &&
    obj.options !== null
  );
};

const elementId = (prefix: string, idx: number): string =>
  `${prefix}-${idx.toString(36)}`;

/**
 * Translates a legacy typed panel into the free-form elements shape:
 *  - lower_third → background shape + title text + (optional) subtitle text
 *  - label → background shape + text
 *  - stats → background shape + N×(label + value) text pairs, horizontal or vertical
 *
 * Returns the input unchanged when it's already free-form. Layout uses
 * design-space coordinates (assumes 1920×1080 default canvas).
 */
const convertLegacyPanel = (legacy: ILegacyPanel): IStudioPanel => {
  const base = {
    id: legacy.id,
    label: legacy.label,
    active: legacy.active,
  };

  if (legacy.type === EStudioPanelType.LOWER_THIRD) {
    const opts = legacy.options;
    const elements: IOverlayElement[] = [];
    if (opts.backgroundColor) {
      elements.push({
        id: elementId("bg", 0),
        kind: "shape",
        bounds: { left: 0, top: 0, width: 1920, height: 1080, zIndex: 0 },
        options: {
          shape: "rect",
          fill: opts.backgroundColor,
        },
      });
    }
    elements.push({
      id: elementId("title", 1),
      kind: "text",
      bounds: { left: 80, top: 720, width: 1500, height: 140, zIndex: 1 },
      options: {
        text: opts.title,
        fontColor: opts.titleColor ?? "#ffffff",
        fontFamily: opts.fontFamily,
        fontSize: "96px",
        bold: true,
        textAlign: opts.align,
      },
    });
    if (opts.subtitle) {
      elements.push({
        id: elementId("sub", 2),
        kind: "text",
        bounds: { left: 80, top: 880, width: 1500, height: 80, zIndex: 2 },
        options: {
          text: opts.subtitle,
          fontColor: opts.subtitleColor ?? "#dddddd",
          fontFamily: opts.fontFamily,
          fontSize: "48px",
          textAlign: opts.align,
        },
      });
    }
    return {
      ...base,
      options: {
        elements,
        background: { image: opts.backgroundImage },
      },
    };
  }

  if (legacy.type === EStudioPanelType.LABEL) {
    const opts = legacy.options;
    const elements: IOverlayElement[] = [];
    if (opts.backgroundColor) {
      elements.push({
        id: elementId("bg", 0),
        kind: "shape",
        bounds: { left: 0, top: 0, width: 1920, height: 1080, zIndex: 0 },
        options: {
          shape:
            opts.borderRadius && opts.borderRadius > 0
              ? "rounded-rect"
              : "rect",
          fill: opts.backgroundColor,
          borderRadius: opts.borderRadius,
        },
      });
    }
    elements.push({
      id: elementId("text", 1),
      kind: "text",
      bounds: { left: 0, top: 0, width: 1920, height: 1080, zIndex: 1 },
      options: {
        text: opts.text,
        fontColor: opts.fontColor ?? "#ffffff",
        fontFamily: opts.fontFamily,
        fontSize: opts.fontSize ?? "64px",
        bold: true,
        textAlign: "center",
      },
    });
    return { ...base, options: { elements } };
  }

  // STATS
  const opts = legacy.options;
  const elements: IOverlayElement[] = [];
  if (opts.backgroundColor) {
    elements.push({
      id: elementId("bg", 0),
      kind: "shape",
      bounds: { left: 0, top: 0, width: 1920, height: 1080, zIndex: 0 },
      options: { shape: "rect", fill: opts.backgroundColor },
    });
  }
  const horizontal = opts.layout === "horizontal";
  const cellW = horizontal
    ? Math.floor(1920 / Math.max(1, opts.fields.length))
    : 1920;
  const cellH = horizontal
    ? 1080
    : Math.floor(1080 / Math.max(1, opts.fields.length));
  opts.fields.forEach((field, i) => {
    const left = horizontal ? i * cellW : 0;
    const top = horizontal ? 0 : i * cellH;
    // value (large)
    elements.push({
      id: elementId("val", i * 2 + 1),
      kind: "text",
      bounds: {
        left,
        top,
        width: cellW,
        height: Math.floor(cellH * 0.7),
        zIndex: 2,
      },
      options: {
        text: field.value,
        fontColor: opts.fontColor ?? "#ffffff",
        fontFamily: opts.fontFamily,
        fontSize: "120px",
        bold: true,
        textAlign: "center",
      },
    });
    // label (small)
    elements.push({
      id: elementId("lab", i * 2 + 2),
      kind: "text",
      bounds: {
        left,
        top: top + Math.floor(cellH * 0.7),
        width: cellW,
        height: Math.floor(cellH * 0.3),
        zIndex: 2,
      },
      options: {
        text: field.label,
        fontColor: opts.fontColor ?? "#bbbbbb",
        fontFamily: opts.fontFamily,
        fontSize: "36px",
        textAlign: "center",
      },
    });
  });
  return { ...base, options: { elements } };
};

/**
 * Public migration helper. Idempotent: returns free-form panels untouched
 * and converts legacy typed panels on read. Apply at every "panel ingress"
 * point: library hydration, area template load, store template install.
 */
export const migrateLegacyPanel = (
  input: IStudioPanel | unknown,
): IStudioPanel => {
  if (isLegacyPanel(input)) return convertLegacyPanel(input);
  return input as IStudioPanel;
};
